/** Bilingual study notes: Kalapurusha - zodiac as cosmic body map. */

/** @typedef {'fire' | 'earth' | 'air' | 'water'} TatvaKey */
/** @typedef {'chest' | 'digestive' | 'sensitive'} WatchKind */

/**
 * @typedef {{
 *   num: number;
 *   signHi: string;
 *   signEn: string;
 *   tatva: TatvaKey;
 *   bodyHi: string;
 *   bodyEn: string;
 *   watch?: WatchKind;
 *   extraHi?: string;
 *   extraEn?: string;
 * }} KalapurushaSignRow
 */

/** @type {Record<TatvaKey, { hi: string; en: string }>} */
export const KALAPURUSHA_TATVA = {
  fire: { hi: 'अग्नि', en: 'Fire' },
  earth: { hi: 'पृथ्वी', en: 'Earth' },
  air: { hi: 'वायु', en: 'Air' },
  water: { hi: 'जल', en: 'Water' },
};

/** @type {KalapurushaSignRow[]} */
export const KALAPURUSHA_SIGNS = [
  {
    num: 1,
    signHi: 'मेष',
    signEn: 'Aries',
    tatva: 'fire',
    bodyHi: 'सिर का ऊपरी और पिछला भाग',
    bodyEn: 'Top and back of the head',
  },
  {
    num: 2,
    signHi: 'वृषभ',
    signEn: 'Taurus',
    tatva: 'earth',
    bodyHi: 'मुख और गला',
    bodyEn: 'Face and throat',
  },
  {
    num: 3,
    signHi: 'मिथुन',
    signEn: 'Gemini',
    tatva: 'air',
    bodyHi: 'गर्दन, कंधे, भुजाएँ, हाथ',
    bodyEn: 'Neck, shoulders, arms, hands',
  },
  {
    num: 4,
    signHi: 'कर्क',
    signEn: 'Cancer',
    tatva: 'water',
    bodyHi: 'वक्ष, हृदय और फेफड़े',
    bodyEn: 'Chest, heart, and lungs',
    watch: 'chest',
    extraHi: 'पाप ग्रह, १२वें या ८वें स्वामी यहाँ? उस क्षेत्र पर स्वास्थ्य-नज़र रखें।',
    extraEn: 'Malefics, 12th lord, or 8th lord here? Keep a health watch on that zone.',
  },
  {
    num: 5,
    signHi: 'सिंह',
    signEn: 'Leo',
    tatva: 'fire',
    bodyHi: 'ऊपरी उदर - जठर, यकृत, पित्ताशय',
    bodyEn: 'Upper abdomen - stomach, liver, gallbladder',
  },
  {
    num: 6,
    signHi: 'कन्या',
    signEn: 'Virgo',
    tatva: 'earth',
    bodyHi: 'मध्य उदर - आंत, अग्न्याशय, पाचन',
    bodyEn: 'Mid abdomen - intestines, pancreas, digestion',
  },
  {
    num: 7,
    signHi: 'तुला',
    signEn: 'Libra',
    tatva: 'air',
    bodyHi: 'निचला उदर - वृक्क, प्रजनन संतुलन',
    bodyEn: 'Lower abdomen - kidneys, reproductive balance',
    watch: 'digestive',
    extraHi: 'पाप ग्रह यहाँ? पाचन-संरक्षण ज़रूरी।',
    extraEn: 'Malefics here? Digestive care matters.',
  },
  {
    num: 8,
    signHi: 'वृश्चिक',
    signEn: 'Scorpio',
    tatva: 'water',
    bodyHi: 'श्रोणि और गुप्त प्रणालियाँ',
    bodyEn: 'Pelvis and private systems',
    watch: 'sensitive',
    extraHi: 'शरीर का सबसे संवेदनशील क्षेत्र - गहरी, निजी प्रणालियाँ। पाप ग्रह? अतिरिक्त सावधानी।',
    extraEn: 'The most sensitive zone - the body’s deepest, most private systems. Malefics? Extra care.',
  },
  {
    num: 9,
    signHi: 'धनु',
    signEn: 'Sagittarius',
    tatva: 'fire',
    bodyHi: 'जंघा और कूल्हे',
    bodyEn: 'Thighs and hips',
    extraHi: 'अग्नि राशि - पर स्वामी गुरु भी है; तत्व का रंग यहाँ भी मायने रखता है।',
    extraEn: 'A fire sign - yet Jupiter also rules it. Element still colours the reading.',
  },
  {
    num: 10,
    signHi: 'मकर',
    signEn: 'Capricorn',
    tatva: 'earth',
    bodyHi: 'घुटने और अस्थि',
    bodyEn: 'Knees and bones',
  },
  {
    num: 11,
    signHi: 'कुम्भ',
    signEn: 'Aquarius',
    tatva: 'air',
    bodyHi: 'पिंडली, संचार, तंत्रिका',
    bodyEn: 'Calves, circulation, nerves',
  },
  {
    num: 12,
    signHi: 'मीन',
    signEn: 'Pisces',
    tatva: 'water',
    bodyHi: 'पैर और लसीका तंत्र',
    bodyEn: 'Feet and lymphatic system',
  },
];

/** @type {{ hookHi: string; hookEn: string; chartCtaHi: string; chartCtaEn: string; conceptHi: string; conceptEn: string; conceptLeadHi: string; conceptLeadEn: string; patternHi: string; patternEn: string; stepsTitleHi: string; stepsTitleEn: string; stepsHi: string[]; stepsEn: string[]; deeperTitleHi: string; deeperTitleEn: string; deeperHi: string; deeperEn: string; closingHi: string; closingEn: string; maleficsHi: string; maleficsEn: string }} */
export const KALAPURUSHA_PAGE = {
  hookHi: 'हमारा शरीर राशि-चक्र पर बँटा है। बारह राशियाँ, बारह अंग - एक पुराना नक्शा 👇',
  hookEn: 'Your body maps onto the zodiac. Twelve signs. Twelve zones. One old map 👇',
  chartCtaHi: 'पढ़ते समय अपनी कुंडली खोलें। बाद में खुद को धन्यवाद देना मत भूलिएगा ↓',
  chartCtaEn: 'Open your chart while you read. You’ll thank yourself later ↓',
  conceptHi: 'यह कालपुरुष कहलाता है - राशि-चक्र एक विशाल शरीर की तरह।',
  conceptEn: 'This is Kalapurusha - the zodiac as one cosmic body.',
  conceptLeadHi: 'मेष = मस्तक। मीन = चरण। आपके ग्रह इसी शरीर पर बैठते हैं - जिस राशि में ग्रह है, वह अंग प्रभावित हो सकता है।',
  conceptLeadEn: 'Aries = the head. Pisces = the feet. Your planets sit on this body - the sign they occupy can touch that body zone.',
  patternHi: 'देखिए नमूना: ऊपर से नीचे - मेष से मीन। राशि-चक्र मानव शरीर को दर्पण की तरह दिखाता है।',
  patternEn: 'See the pattern: head to feet, Aries to Pisces. The zodiac mirrors the human body, top to bottom.',
  stepsTitleHi: 'अपनी कुंडली में कैसे लगाएँ',
  stepsTitleEn: 'How to use this in your chart',
  stepsHi: [
    'पाप ग्रह ढूँढें - शनि, मंगल, राहु, केतु।',
    'उनकी राशि नोट करें।',
    'ऊपर वाली सूची से शरीर-अंग मिलाएँ।',
    'उस क्षेत्र पर अतिरिक्त ध्यान - खासकर उस ग्रह की दशा में।',
  ],
  stepsEn: [
    'Find malefics - Saturn, Mars, Rahu, Ketu.',
    'Note their sign.',
    'Match the sign to its body part above.',
    'Give that area extra care - especially during that planet’s Dasha.',
  ],
  deeperTitleHi: 'एक गहरी परत',
  deeperTitleEn: 'A deeper layer',
  deeperHi:
    'जल राशि में पाप ग्रह - जैसे वृश्चिक या मीन - शारीरिक क्षेत्र को अग्नि राशि में उसी ग्रह से कहीं तेज़ मारता है। धनु अग्नि है, पर गुरु का भी घर है - तत्व और स्वामी, दोनों गिनें।',
  deeperEn:
    'A malefic in a watery sign - like Scorpio or Pisces - often hits the body part harder than the same planet in a fiery sign. Sagittarius is fire, yet Jupiter also owns it - count both element and lordship.',
  closingHi: 'आपकी कुंडली सिर्फ तारीखें और भाव नहीं - यह आपके शरीर का नक्शा है।',
  closingEn: 'Your chart isn’t just dates and houses. It’s a map of your own body.',
  imageAltHi: 'कालपुरुष मंडल - बारह राशियाँ और शरीर-अंग',
  imageAltEn: 'Kalapurusha mandala - twelve signs mapped on the cosmic body',
  imageCaptionHi: 'मेष से मीन - शिर से चरण तक। चित्र के रंग = तत्व।',
  imageCaptionEn: 'Aries to Pisces - head to feet. Colours in the chart = elements.',
  maleficsHi: 'पाप ग्रह (इस पाठ में)',
  maleficsEn: 'Malefics (this lesson)',
};
